import classNames from 'classnames';
import React, { useState } from 'react';

import { UserRight } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { useBundle } from '@amzn/react-arb-tools';
import {
  AdvocatesComponent,
  BENTO_CARD_MODAL_STATUS,
  BentoCards,
  Breadcrumbs,
  CardsDashboard,
  CustomGraphic,
  DynamicList,
  HEADING_TAG,
  HeroAsset,
  ICON_NAME,
  Loader,
  LOADER_SIZE,
  Outro,
  PageSection,
  SECTION_THEME,
  Tagline,
  TextComponent,
  useDeviceSize,
} from '@amzn/sss-website-theme';

import carbonRemovalAnimationDesktop from 'src/assets/lotties/carbon-removal-desktop.json';
import carbonRemovalAnimationMobile from 'src/assets/lotties/carbon-removal-mobile.json';
import {
  isRebrandingWeblabEnabled,
  isStorefrontListingEnabled,
  isWeblabSegmentPagesEnabled,
} from 'src/clients/weblab/weblab-client';
import { CRL_DOMAIN } from 'src/constants/env-constants';
import { COMMON_BUNDLE_NAME, SEGMENT_PAGE_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { ROUTES } from 'src/enums';
import { getCreditsAssetRoot, getImageRoot, MEDIA_TYPE } from 'src/helpers';
import { t } from 'src/helpers/translations/translation-helpers';
import { useGetUserRights } from 'src/hooks/useGetUserRights.hook';

import styles from './SegmentPageTemplate.module.scss';
import { SegmentConfigProps } from './types';

interface SegmentPageTemplateProps {
  config: SegmentConfigProps;
  isBundleLoading: boolean;
  breadcrumbTitle?: string;
}

export const SegmentPageTemplate: React.FC<SegmentPageTemplateProps> = ({
  config,
  isBundleLoading,
  breadcrumbTitle,
}) => {
  const [, setBentoCardsModalIsVisible] = useState(false);

  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [segmentPageBundle] = useBundle(SEGMENT_PAGE_BUNDLE_NAME);

  const { isAuthenticated, userRights } = useGetUserRights();

  const handleOpenBentoCard = (newStatus: BENTO_CARD_MODAL_STATUS) => {
    setBentoCardsModalIsVisible(newStatus !== BENTO_CARD_MODAL_STATUS.None);
  };

  if (isBundleLoading) {
    return <Loader size={LOADER_SIZE.LARGE} theme={SECTION_THEME.Light} />;
  }

  // Determine getStartedCta content based on userRights loading state
  let getStartedCta;
  if (!userRights.length) {
    getStartedCta = {
      label: t(segmentPageBundle, 'outro_cta_getStarted_label'),
      link: '#',
      forceServerSideNav: false,
    };
  } else {
    // Show appropriate CTA based on user authentication and rights
    const isKatharineApproved = isAuthenticated && userRights.includes(UserRight.KATHARINE_APPROVED);
    const isKatharinePrescreened = isAuthenticated && userRights.includes(UserRight.KATHARINE_PRESCREENED);
    const canAccessStorefront = isKatharineApproved || isKatharinePrescreened;

    const getStartedCtaLabel = canAccessStorefront
      ? t(segmentPageBundle, 'outro_cta_viewAvailableCredits_label')
      : t(segmentPageBundle, 'outro_cta_getStarted_label');

    const storefrontRoute = isStorefrontListingEnabled ? ROUTES.CREDITS_STORE : ROUTES.CREDITS_STOREFRONT;
    const getStartedCtaLink = canAccessStorefront ? storefrontRoute : ROUTES.INTAKE_PAGE;

    getStartedCta = {
      label: getStartedCtaLabel,
      link: getStartedCtaLink,
      forceServerSideNav: true,
    };
  }

  return (
    <>
      {breadcrumbTitle && (
        <Breadcrumbs
          homepageLink={{ label: t(commonBundle, 'homepage_label'), url: '/' }}
          parentLink={{ label: t(commonBundle, 'carbon_credit_service_label'), url: ROUTES.CREDITS_LANDING }}
          currentPage={breadcrumbTitle}
        />
      )}
      <PageSection theme={isRebrandingWeblabEnabled ? 'light-spruce' : 'grey-secondary'} className={styles.segment}>
        {config.heroAsset && (
          <HeroAsset
            className={styles.heroAsset}
            title={config.heroAsset.title}
            subtitle={config.heroAsset.subtitle ? config.heroAsset.subtitle : undefined}
            image={config.heroAsset.image}
            isRebrandingActive={isRebrandingWeblabEnabled}
          />
        )}

        {config.headingSection && (
          // TODO create component for this https://issues.amazon.com/issues/ASX-486
          <div data-track-label={config.headingSection.tagline} className={styles.headingSection}>
            <Tagline
              isRebrandingActive={isRebrandingWeblabEnabled}
              className={classNames(styles.tagline, isRebrandingWeblabEnabled && styles.rebranding)}
            >
              {config.headingSection.tagline}
            </Tagline>
            <TextComponent
              isRebrandingActive={isRebrandingWeblabEnabled}
              className={styles.textComponent}
              headingClassName={styles.textComponentHeading}
              contentClassName={styles.textComponentContent}
              headingSize={HEADING_TAG.H3}
              theme={SECTION_THEME.Light}
              heading={config.headingSection.heading}
              copy={config.headingSection.description}
              id="text-component"
              openNewTabString={t(commonBundle, 'newTab_label')}
            />
          </div>
        )}

        {config.primaryBentoCards && (
          <BentoCards
            isRebrandingActive={isRebrandingWeblabEnabled}
            cardsWrapperProps={{
              openNewTabLabel: t(commonBundle, 'newTab_label'),
              sliderPaginationLabel: t(commonBundle, 'of_label'),
              sliderNextAriaLabel: t(commonBundle, 'nextSlide_ariaLabel'),
              sliderPreviousAriaLabel: t(commonBundle, 'previousSlide_ariaLabel'),
            }}
            sourceLabel={t(commonBundle, 'source_label')}
            hideImageCardsOnMobile
            onBentoModalChange={handleOpenBentoCard}
            {...config.primaryBentoCards.bentoCardProps}
          />
        )}

        {config.cardsDashboard && (
          <CardsDashboard
            firstCard={config.cardsDashboard?.firstCard}
            secondCard={config.cardsDashboard.secondCard}
            thirdCard={config.cardsDashboard.thirdCard}
            fourthCard={config.cardsDashboard?.fourthCard}
            isRebrandingActive={isRebrandingWeblabEnabled}
          />
        )}

        {config.hasAnimatedGraphic && (
          <CustomGraphic
            isRebrandingActive={isRebrandingWeblabEnabled}
            lottieStrings={{
              pause: t(commonBundle, 'pauseAnimation_ariaLabel'),
              play: t(commonBundle, 'playAnimation_ariaLabel'),
            }}
            openNewTabString={t(commonBundle, 'newTab_label')}
            footerCtaAriaLabel={t(segmentPageBundle, 'customGraphic_footer_cta_ariaLabel')}
            className={styles.customGraphic}
            tagline={t(segmentPageBundle, 'customGraphic_theChallenge_tagline')}
            heading={t(segmentPageBundle, 'customGraphic_scalingCarbonRemoval_heading')}
            copy={t(segmentPageBundle, 'customGraphic_scalingCarbonRemoval_description')}
            lottieAnimation={isSmallDesktop ? carbonRemovalAnimationDesktop : carbonRemovalAnimationMobile}
            lottieAlt={t(segmentPageBundle, 'customGraphic_lottieAnimation_altText')}
            emissionsCopy={t(segmentPageBundle, 'customGraphic_emissionsCopy')}
            details={{
              detailFirst: t(segmentPageBundle, 'customGraphic_details_fossilFuel'),
              detailSecond: t(segmentPageBundle, 'customGraphic_details_deforestation'),
              detailThird: t(segmentPageBundle, 'customGraphic_details_natureBased'),
              detailFourth: t(segmentPageBundle, 'customGraphic_details_technological'),
              detailFifth: t(segmentPageBundle, 'customGraphic_details_netEmissions'),
            }}
            footerContent={{
              footerParagraph: t(segmentPageBundle, 'customGraphic_footer_dataSource'),
              footerLinkLabel: t(segmentPageBundle, 'customGraphic_footer_linkLabel'),
              footerLinkHref: t(segmentPageBundle, 'customGraphic_footer_linkHref'),
              footerCTA: t(segmentPageBundle, 'customGraphic_footer_cta'),
            }}
            modalContent={{
              image: {
                src: isSmallDesktop
                  ? `${CRL_DOMAIN}/credits/locale/en-us/resource-type/image/variation/desktop/custom-graphic-modal-desktop.png`
                  : `${CRL_DOMAIN}/credits/locale/en-us/resource-type/image/variation/mobile/custom-graphic-modal-mobile.png`,
                alt: t(segmentPageBundle, 'customGraphic_lottieAnimation_altText'),
              },
              title: t(segmentPageBundle, 'customGraphic_modal_title'),
              copy: t(segmentPageBundle, 'customGraphic_modal_description'),
              tagline: t(segmentPageBundle, 'customGraphic_modal_tagline'),
              footerContent: {
                iconName: ICON_NAME.INFO,
                link: {
                  linkCopy: t(segmentPageBundle, 'customGraphic_footer_linkLabel'),
                  linkHref: t(segmentPageBundle, 'customGraphic_footer_linkHref'),
                },
                copy: t(segmentPageBundle, 'customGraphic_footer_dataSource'),
              },
            }}
          />
        )}

        {config.dynamicList && (
          <DynamicList
            className={styles.dynamicList}
            openNewTabString={t(commonBundle, 'newTab_label')}
            theme={SECTION_THEME.Light}
            {...config.dynamicList}
            isRebrandingActive={isRebrandingWeblabEnabled}
          />
        )}

        {config.secondaryBentoCards && (
          <BentoCards
            isRebrandingActive={isRebrandingWeblabEnabled}
            className={classNames(!isRebrandingWeblabEnabled && styles.secondaryBentoCards)}
            cardsWrapperProps={{
              openNewTabLabel: t(commonBundle, 'newTab_label'),
              sliderPaginationLabel: t(commonBundle, 'of_label'),
              sliderNextAriaLabel: t(commonBundle, 'nextSlide_ariaLabel'),
              sliderPreviousAriaLabel: t(commonBundle, 'previousSlide_ariaLabel'),
            }}
            sourceLabel={t(commonBundle, 'source_label')}
            onBentoModalChange={handleOpenBentoCard}
            {...config.secondaryBentoCards.bentoCardProps}
          />
        )}
      </PageSection>

      <AdvocatesComponent
        scrollLabel={t(commonBundle, 'scrollDown_ariaLabel')}
        dataTrackingLabel={t(segmentPageBundle, 'advocates_dataTrackingLabel')}
        theme={SECTION_THEME.Squid_Ink}
        isStandAlone
        brandTabs={{
          buttonsAriaLabels: {
            previous: t(commonBundle, 'previous_ariaLabel'),
            next: t(commonBundle, 'next_ariaLabel'),
          },
          options: [
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-3.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_krupp'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-3.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_krupp'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_krupp'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_krupp'),
            },
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-2.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_figueres'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-2.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_figueres'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_figueres'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_figueres'),
            },
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-1.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_sanjayan'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-1.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_sanjayan'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_sanjayan'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_sanjayan'),
            },
          ],
        }}
        quotes={[
          t(segmentPageBundle, 'advocates_quote_sanjayan'),
          t(segmentPageBundle, 'advocates_quote_figueres'),
          t(segmentPageBundle, 'advocates_quote_krupp'),
        ]}
        backgroundImage={{
          src: `${imageRoot}/credits-advocates-1.jpeg`,
          alt: t(segmentPageBundle, 'advocates_backgroundImage_altText'),
        }}
      />

      <Outro
        outroTitle={t(segmentPageBundle, 'outro_title')}
        theme={SECTION_THEME.Squid_Ink}
        isRebrandingActive={isWeblabSegmentPagesEnabled}
        ctas={[
          {
            label: t(segmentPageBundle, 'outro_cta_faq_label'),
            url: ROUTES.CREDITS_FAQ,
            // FAQ page is in a separate app so force server side nav.
            forceServerSideNav: true,
          },
          {
            label: getStartedCta.label,
            url: getStartedCta.link,
            forceServerSideNav: getStartedCta.forceServerSideNav,
          },
        ]}
        link={{
          label: t(segmentPageBundle, 'outro_link_californiaDisclosure_label'),
          url: `${getCreditsAssetRoot(isSmallDesktop, MEDIA_TYPE.PDF)}/California%20AB%201305%20Disclosure.pdf`,
        }}
      />
    </>
  );
};
